"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPlanningGridApiResponseSchema = exports.planningGridRowSchema = exports.planningGridCellSchema = exports.planningGridAbsenceCellSchema = exports.planningGridRestCellSchema = exports.planningGridSlotCellSchema = exports.planningGridOverriddenSlotSchema = exports.planningSlotChangeSchema = exports.planningSlotChangeSideSchema = void 0;
const zod_1 = require("zod");
const absence_enums_1 = require("./absence-enums");
const enums_1 = require("./enums");
const override_slot_1 = require("./override-slot");
const pauses_1 = require("./pauses");
const isoWeekSchema = zod_1.z.string().regex(/^\d{4}-W\d{2}$/);
const timeStringSchema = zod_1.z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/);
/** One side (before/after) of a noted change, ready to render as a label. */
exports.planningSlotChangeSideSchema = zod_1.z.object({
    slotTypeLabel: zod_1.z.string(),
    startTime: timeStringSchema,
    endTime: timeStringSchema,
    status: enums_1.plannedSlotStatusSchema,
});
/** The latest recorded change on a slot (before → after, who, when). */
exports.planningSlotChangeSchema = zod_1.z.object({
    changeType: enums_1.overrideChangeTypeSchema,
    editedAt: zod_1.z.string(),
    editedBy: zod_1.z.string().nullable(),
    before: exports.planningSlotChangeSideSchema,
    after: exports.planningSlotChangeSideSchema,
});
/**
 * The originally planned slot a rest/absence cell superseded (an explicitly
 * cancelled slot, or the slot an approved absence covers), so the grid can
 * show what the override is overriding.
 */
exports.planningGridOverriddenSlotSchema = zod_1.z.object({
    label: zod_1.z.string(),
    startTime: timeStringSchema,
    endTime: timeStringSchema,
});
const toMinutes = (time) => {
    const [hours = 0, minutes = 0] = time.split(':').map(Number);
    return hours * 60 + minutes;
};
exports.planningGridSlotCellSchema = zod_1.z.object({
    type: zod_1.z.literal('slot'),
    slotId: zod_1.z.string().uuid(),
    slotTypeId: zod_1.z.string().uuid(),
    label: zod_1.z.string(),
    startTime: timeStringSchema,
    endTime: timeStringSchema,
    workedMinutes: zod_1.z.number().int().min(0),
    // The slot's working periods. `startTime`/`endTime` are their envelope and
    // `workedMinutes` their summed duration. Optional for backward compatibility;
    // the API always sends it (a single-range envelope when not split).
    timeRanges: override_slot_1.timeRangesSchema.optional(),
    source: enums_1.slotSourceSchema,
    status: enums_1.plannedSlotStatusSchema,
    officeId: zod_1.z.string().uuid(),
    officeName: zod_1.z.string(),
    // Optional for backward compatibility with older payloads / mocks; the API
    // always sends them (null when there is no note / no recorded change).
    note: zod_1.z.string().nullable().optional(),
    change: exports.planningSlotChangeSchema.nullable().optional(),
    // Pauses carved out of this slot (worked time, excluded from office
    // presence). Optional for backward compatibility; the API always sends it
    // (empty array when there are none).
    pauses: zod_1.z.array(pauses_1.assistantSlotPauseSchema).optional(),
});
exports.planningGridRestCellSchema = zod_1.z.object({
    type: zod_1.z.literal('rest'),
    // The explicitly cancelled slot this rest day replaced, or null/absent when
    // the day was never planned. Optional for backward compatibility.
    overriddenSlot: exports.planningGridOverriddenSlotSchema.nullable().optional(),
});
exports.planningGridAbsenceCellSchema = zod_1.z.object({
    type: zod_1.z.literal('absence'),
    absenceRequestId: zod_1.z.string().uuid(),
    absenceType: absence_enums_1.absenceTypeSchema,
    // The planned slot the absence covers, or null/absent when the assistant was
    // not scheduled that day. Optional for backward compatibility.
    overriddenSlot: exports.planningGridOverriddenSlotSchema.nullable().optional(),
    // Minutes this absence contributes to the weekly worked total: the covered
    // slot's worked minutes when the absence is compensated, 0 when it is unpaid
    // or covers no planned slot. Optional for backward compatibility; the API
    // always sends it.
    countedMinutes: zod_1.z.number().int().min(0).optional(),
});
exports.planningGridCellSchema = zod_1.z
    .discriminatedUnion('type', [
    exports.planningGridSlotCellSchema,
    exports.planningGridRestCellSchema,
    exports.planningGridAbsenceCellSchema,
])
    .refine((cell) => cell.type !== 'slot' ||
    toMinutes(cell.endTime) > toMinutes(cell.startTime), { message: 'endTime must be after startTime', path: ['endTime'] });
exports.planningGridRowSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    userId: zod_1.z.string().uuid(),
    firstname: zod_1.z.string(),
    lastname: zod_1.z.string(),
    contractType: enums_1.contractTypeSchema,
    templateWeek: enums_1.templateWeekSchema.nullable(),
    weeklyContractHours: zod_1.z.number(),
    weeklyActualMinutes: zod_1.z.number().int().min(0),
    offices: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string().uuid(), name: zod_1.z.string() })),
    // ISO timestamp of the assistant's deactivation, null when they are active. A
    // deactivated row is history kept visible: the UI renders it read-only.
    // Optional for backward compatibility with older payloads / mocks; the API
    // always sends it.
    deactivatedAt: zod_1.z.string().nullable().optional(),
    cells: zod_1.z.array(exports.planningGridCellSchema).length(7),
});
exports.getPlanningGridApiResponseSchema = zod_1.z.object({
    week: isoWeekSchema,
    rows: zod_1.z.array(exports.planningGridRowSchema),
});
